import * as React from 'react'
import {
  Alarm,
  ArrowsClockwise,
  ArrowRight,
  CalendarPlus,
  Eye,
  PencilSimple,
  Trash,
  EyeSlash,
} from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import {
  Card,
  CardHeader,
  CardTitle,
  CardAction,
  CardContent,
} from '../core/card'
import { Badge } from '../core/badge'
import { EventAgenda } from '../core/event-agenda'
import { type CalendarEvent } from '../core/event-calendar-utils'
import { Skeleton } from '../core/skeleton'
import { WidgetMenu, type WidgetMenuItem } from './widget-menu'

/**
 * UpcomingWidget — the homepage "Upcoming" card: the next reminders due, grouped
 * under Today / Tomorrow / date headers with their due time and a recurrence
 * badge, with a subtle rule between date sections. A thin shell over {@link EventAgenda} (grouped) —
 * reminders map to dotless agenda rows (no color = no dot), and each recurring
 * reminder's cadence rides in the row's trailing slot. On row hover, view / edit
 * / remove actions reveal (as in `EventAgenda`), wired to the corresponding
 * callbacks.
 *
 *   <UpcomingWidget reminders={reminders} onView={open} onEdit={edit} onRemove={del} />
 *
 * The time-based counterpart to the navigation-history
 * {@link RecentlyVisitedWidget}.
 */

export interface UpcomingReminder {
  /** Stable unique id. */
  id: string
  /** Reminder title. */
  title: string
  /** When the reminder is next due. */
  dueAt: Date
  /** All-day reminder — shows "All day" instead of a time. */
  allDay?: boolean
  /** Recurrence cadence shown as a trailing badge, e.g. "Weekly"; omit for one-off. */
  recurrence?: string
}

export interface UpcomingWidgetProps {
  reminders: UpcomingReminder[]
  /** "Now", for the Today/Tomorrow labels + the due-from cutoff. Defaults to now. */
  now?: Date
  /** Max reminders to list (default 8). */
  maxItems?: number
  /**
   * View a reminder. Shown as a hover Eye action when `onEdit`/`onRemove` are
   * also set; when it's the only action, the whole row is clickable instead.
   */
  onView?: (reminder: UpcomingReminder) => void
  /** Edit a reminder (hover action). */
  onEdit?: (reminder: UpcomingReminder) => void
  /** Remove a reminder (hover action, red hover). */
  onRemove?: (reminder: UpcomingReminder) => void
  /** Wire the ⋯ menu's "Schedule reminder" action (opens the reminder composer); shown first, hidden when omitted. */
  onSchedule?: () => void
  /** Wire the ⋯ menu's "View all" action (navigates to the full reminders list); hidden when omitted. */
  onViewAll?: () => void
  /** Wire the ⋯ menu's "Hide widget" action; hidden when omitted. */
  onHide?: () => void
  /** Density — `md` (default) or `sm` (compact rows, for a tight dashboard column). */
  size?: 'sm' | 'md'
  /** Card heading. */
  title?: React.ReactNode
  /** Text shown when there are no upcoming reminders. */
  emptyLabel?: string
  /** Show a loading skeleton (agenda-row placeholders) instead of reminders. */
  loading?: boolean
  className?: string
}

const actionButtonClass =
  'inline-flex size-7 items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-gray-100 hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-default [&>svg]:size-4'
const removeButtonClass =
  'inline-flex size-7 items-center justify-center rounded-md text-fbui-gray-500 outline-none transition-colors hover:bg-fbui-red-50 hover:text-fbui-red-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-fbui-ring-default [&>svg]:size-4'

export const UpcomingWidget = React.forwardRef<
  HTMLDivElement,
  UpcomingWidgetProps
>(
  (
    {
      reminders,
      now,
      maxItems = 8,
      onView,
      onEdit,
      onRemove,
      onSchedule,
      onViewAll,
      onHide,
      size = 'md',
      title = 'Upcoming',
      emptyLabel = 'Nothing here yet',
      loading,
      className,
    },
    ref
  ) => {
    if (loading) {
      return (
        <Card
          ref={ref}
          size="sm"
          className={cn('flex max-h-52 w-full max-w-md flex-col', className)}
        >
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Alarm aria-hidden="true" className="size-4 text-fbui-gray-500" />
              {title}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col">
            <div className="flex min-h-0 flex-1 flex-col gap-[0.75rem]">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-[0.75rem]">
                  <Skeleton className="size-8 shrink-0 rounded-lg" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-3.5 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )
    }
    // Reminders → agenda events: start = due date, no color (dotless rows). Keep
    // a map back to the reminder for the recurrence badge + row actions.
    const byId = new Map(reminders.map((r) => [r.id, r]))
    const events: CalendarEvent[] = reminders.map((r) => ({
      id: r.id,
      title: r.title,
      start: r.dueAt,
      allDay: r.allDay,
    }))

    // Edit/remove are the "other" hover actions. When present, the row is a
    // static group whose hover actions (incl. a View eye) carry every action —
    // `EventAgenda`/`AgendaRow` treat `actions` and a whole-row `onClick` as
    // mutually exclusive. With only `onView`, we pass no `actions` and let the
    // whole row be the clickable view affordance instead (see onEventClick).
    const hasOtherActions = Boolean(onEdit || onRemove)

    const menuItems: WidgetMenuItem[] = [
      ...(onSchedule
        ? [{ key: 'schedule', label: 'Schedule reminder', icon: <CalendarPlus />, onSelect: onSchedule }]
        : []),
      ...(onViewAll
        ? [{ key: 'view-all', label: 'View all', icon: <ArrowRight />, onSelect: onViewAll }]
        : []),
      ...(onHide
        ? [{ key: 'hide', label: 'Hide widget', icon: <EyeSlash />, onSelect: onHide }]
        : []),
    ]

    return (
      <Card ref={ref} size="sm" className={cn('flex max-h-52 w-full max-w-md flex-col', className)}>
        {/* Tighter title→content gap than the Card default (p-4 bottom). */}
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Alarm aria-hidden="true" className="size-4 text-fbui-gray-500" />
            {title}
          </CardTitle>
          {menuItems.length > 0 && (
            <CardAction>
              <WidgetMenu label="Upcoming options" items={menuItems} />
            </CardAction>
          )}
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          {/* Scroll on an inner wrapper so the card's bottom padding stays below
              the scroll region (matching RecentlyVisitedWidget) instead of being
              clipped when the agenda overflows the card height. */}
          <div className="fbui-scroll flex min-h-0 flex-1 flex-col overflow-y-auto">
            <EventAgenda
            events={events}
            variant="grouped"
            dividers
            size={size}
            now={now}
            maxEvents={maxItems}
            emptyLabel={emptyLabel}
            // Whole-row click opens the reminder only when there are no
            // edit/remove hover actions (otherwise the View action carries it).
            onEventClick={
              !hasOtherActions && onView
                ? (event) => {
                    const reminder = byId.get(event.id)
                    if (reminder) onView(reminder)
                  }
                : undefined
            }
            renderTrailing={(event) => {
              const recurrence = byId.get(event.id)?.recurrence
              return recurrence ? (
                <Badge color="gray" size="sm" prefixIcon={<ArrowsClockwise />}>
                  {recurrence}
                </Badge>
              ) : null
            }}
            rowActions={
              hasOtherActions
                ? (event) => {
                    const reminder = byId.get(event.id)
                    if (!reminder) return null
                    return (
                      <>
                        {onView && (
                          <button
                            type="button"
                            aria-label={`View ${reminder.title}`}
                            onClick={() => onView(reminder)}
                            className={actionButtonClass}
                          >
                            <Eye />
                          </button>
                        )}
                        {onEdit && (
                          <button
                            type="button"
                            aria-label={`Edit ${reminder.title}`}
                            onClick={() => onEdit(reminder)}
                            className={actionButtonClass}
                          >
                            <PencilSimple />
                          </button>
                        )}
                        {onRemove && (
                          <button
                            type="button"
                            aria-label={`Remove ${reminder.title}`}
                            onClick={() => onRemove(reminder)}
                            className={removeButtonClass}
                          >
                            <Trash />
                          </button>
                        )}
                      </>
                    )
                  }
                : undefined
            }
          />
          </div>
        </CardContent>
      </Card>
    )
  }
)
UpcomingWidget.displayName = 'UpcomingWidget'
